import { canTowVehicle, createJob, approveJob, denyJob, cancelJob } from 'api';

import { ActionFunction } from 'react-router';

export enum JobActionHandler {
  CREATE = 'handleJobSubmit',
  CANCEL = 'handleCancelJob',
  APPROVE = 'handleApproveJob',
  REJECT = 'handleRejectJob',
  OVERRIDE_DNT = 'handleOverrideDNT',
}

export interface JobsActionResponse {
  status: number;
  statusText: string;
  error?: string;
  dntMsg?: string;
  handler: JobActionHandler;
}

const jobsAction: ActionFunction = async ({
  request,
}): Promise<JobsActionResponse> => {
  const {
    handler,
    dntBody,
    tenantID,
    jobBody,
    reason,
    reasonID,
    signature,
    jobID,
  } = await request.json();

  try {
    switch (handler as JobActionHandler) {
      case JobActionHandler.APPROVE: {
        const approveRes = await approveJob({
          tenantID,
          jobID,
          body: signature,
        });
        if (!approveRes.ok) {
          const json = await approveRes.json();
          return {
            status: approveRes.status,
            statusText: approveRes.statusText,
            error: json.message,
            handler: handler,
          };
        }
        return {
          status: approveRes.status,
          statusText: approveRes.statusText,
          handler: handler,
        };
      }
      case JobActionHandler.REJECT: {
        const denyRes = await denyJob({ tenantID, jobID, reason });
        if (!denyRes.ok) {
          const json = await denyRes.json();
          return {
            status: denyRes.status,
            statusText: denyRes.statusText,
            error: json.message,
            handler: handler,
          };
        }
        return {
          status: denyRes.status,
          statusText: denyRes.statusText,
          handler: handler,
        };
      }
      case JobActionHandler.OVERRIDE_DNT: {
        const jobRes = await createJob(tenantID, jobBody);
        if (!jobRes.ok) {
          return {
            status: jobRes.status,
            statusText: jobRes.statusText,
            error: jobRes.statusText,
            handler: handler,
          };
        }

        return {
          status: jobRes.status,
          statusText: jobRes.statusText,
          handler: handler,
        };
      }
      case JobActionHandler.CREATE: {
        const dntRes = await canTowVehicle(tenantID, dntBody);
        const json = await dntRes.json();
        if (dntRes.ok) {
          if (!json.operationApproved) {
            return {
              status: dntRes.status,
              statusText: dntRes.statusText,
              dntMsg: json.message,
              handler: handler,
            };
          }
        } else {
          if (
            json?.message === 'Please provide vin or license plate information'
          ) {
            return {
              status: dntRes.status,
              statusText: dntRes.statusText,
              dntMsg:
                'Could not verify tow eligibility because there is no VIN or license plate number',
              handler: handler,
            };
          }
        }

        const jobRes = await createJob(tenantID, jobBody);
        if (!jobRes.ok) {
          return {
            status: jobRes.status,
            statusText: jobRes.statusText,
            error: jobRes.statusText,
            handler: handler,
          };
        }

        return {
          status: jobRes.status,
          statusText: jobRes.statusText,
          handler: handler,
        };
      }
      case JobActionHandler.CANCEL: {
        const cancelRes = await cancelJob({
          tenantID,
          jobID,
          reasonID,
          reason,
        });
        if (!cancelRes.ok) {
          const json = await cancelRes.json();
          return {
            status: cancelRes.status,
            statusText: cancelRes.statusText,
            error: json.message,
            handler: handler,
          };
        }
        return {
          status: cancelRes.status,
          statusText: cancelRes.statusText,
          handler: handler,
        };
      }
      default:
        return {
          status: 500,
          statusText: 'Internal Server Error',
          error: `unknown handler ${handler} submitted to action`,
          handler: handler,
        };
    }
  } catch (e) {
    const error = e as Error;
    return {
      status: 500,
      statusText: 'Internal Server Error',
      error: error.message,
      handler: handler,
    };
  }

  return {
    status: 500,
    statusText: 'Internal Server Error',
    error: 'Unknown error',
    handler: handler,
  };
};

export default jobsAction;
